import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinDropdown,
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
  BackButton,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loader from "../../../components/partials/Loader";

const AddUnion = () => {
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredTehsils, setFilteredTehsils] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoader(true);
      try {
        // ✅ Only fetch districts initially
        const districtsRes = await fetchData(
          "districts",
          "GET",
          null,
          {},
          {},
          navigate,
          "/districts",
          dispatch
        );
        const allDists = districtsRes?.data || [];
        setDistricts(allDists);
        setFilteredDistricts(allDists.filter((d) => d.status === 1));
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoader(false);
      }
    };

    fetchInitialData();
  }, [navigate, dispatch]);

  const fetchTehsils = async (districtId) => {
    if (!districtId) {
      setFilteredTehsils([]);
      return;
    }

    setLoader(true);
    try {
      const response = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: districtId }, // <-- filter by district
        navigate,
        "/tehsils",
        dispatch
      );
      const allTehsils = response?.data || [];
      setTehsils(allTehsils);
      setFilteredTehsils(allTehsils.filter((t) => t.status === 1));
    } catch (error) {
      toast.error("Failed to load tehsils");
    } finally {
      setLoader(false);
    }
  };

  // ✅ Handle district selection and fetch tehsils accordingly
  const handleDistrictChange = (selectedDistrict) => {
    formik.setValues((prev) => ({
      ...prev,
      District_Id: selectedDistrict,
      Tehsil_Id: "", 
    }));
    fetchTehsils(selectedDistrict);
  };

  const validationSchema = Yup.object({
    UC_Name: Yup.string()
      .max(50, "Union Name cannot exceed 50 characters")
      .required("Union Name is required"),
    // Province_Id: Yup.string().required("Province is required"), // ❌ commented
    // Division_Id: Yup.string().required("Division is required"), // ❌ commented
    District_Id: Yup.string().required("District is required"),
    Tehsil_Id: Yup.string().required("Tehsil is required"),
    Status: Yup.number().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      UC_Name: "",
      // Province_Id: "", // ❌ commented
      // Division_Id: "", // ❌ commented
      District_Id: "",
      Tehsil_Id: "",
      Status: 1,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);

      try {
        if (values.Status == 1) {
          const selectedDistrict = districts.find(
            (d) =>
              d.district_id == values.District_Id ||
              d.District_Id == values.District_Id
          );
          if (selectedDistrict && selectedDistrict.status === 0) {
            toast.error("Cannot activate UC. First activate related District.");
            setIsSubmitting(false);
            return;
          }

          const selectedTehsil = tehsils.find(
            (t) =>
              t.tehsil_id == values.Tehsil_Id || t.Tehsil_Id == values.Tehsil_Id
          );
          if (selectedTehsil && selectedTehsil.status === 0) {
            toast.error("Cannot activate UC. First activate related Tehsil.");
            setIsSubmitting(false);
            return;
          }
        }

        const data = {
          uc_name: values.UC_Name,
          // province_id: values.Province_Id, // ❌ commented
          // division_id: values.Division_Id, // ❌ commented
          district_id: values.District_Id,
          tehsil_id: values.Tehsil_Id,
          status: values.Status,
        };

        const response = await fetchData(
          "ucs",
          "POST",
          data,
          {},
          {},
          navigate,
          "/ucs",
          dispatch
        );
        toast.success(response.message || "UC created successfully");
        formik.resetForm();
        navigate("/ucs");
      } catch (error) {
        toast.error(error.message || "Failed to create UC");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create Union Council
        </h1>
        <BackButton text="Back" to="/ucs" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b mb-4">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            CREATE Union Council
          </h1>
        </div>

        <form className="p-4" onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 p-4 my-4">
            {/* ❌ Province dropdown commented
            <ThinDropdownWithSearch ... />
            */}

            {/* ❌ Division dropdown commented
            <ThinDropdownWithSearch ... />
            */}

            <ThinDropdownWithSearch
              label="District"
              id="district-dropdown"
              options={filteredDistricts.map((district) => ({
                value: district.District_Id || district.district_id,
                label: district.District_Name || district.district_name,
              }))}
              value={formik.values.District_Id}
              onChange={handleDistrictChange}
              error={formik.touched.District_Id && formik.errors.District_Id}
              helperText={
                formik.touched.District_Id && formik.errors.District_Id
              }
            />

            <ThinDropdownWithSearch
              label="Tehsil"
              id="tehsil-dropdown"
              options={filteredTehsils.map((tehsil) => ({
                value: tehsil.Tehsil_Id || tehsil.tehsil_id,
                label: tehsil.Tehsil_Name || tehsil.tehsil_name,
              }))}
              value={formik.values.Tehsil_Id}
              onChange={(value) => formik.setFieldValue("Tehsil_Id", value)}
              disabled={!formik.values.District_Id}
              error={formik.touched.Tehsil_Id && formik.errors.Tehsil_Id}
              helperText={formik.touched.Tehsil_Id && formik.errors.Tehsil_Id}
            />

            <ThinTextField
              id="UC_Name"
              name="UC_Name"
              label="Union Name"
              placeholder="Enter Union Name"
              type="text"
              value={formik.values.UC_Name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.UC_Name && formik.errors.UC_Name}
              helperText={formik.touched.UC_Name && formik.errors.UC_Name}
              maxLength={50}
            />

            {/* ❌ UC Code auto-generate commented out
            <ThinTextField ... />
            */}

            <ThinDropdown
              label="Status"
              id="Status-dropdown"
              name="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.Status}
              onChange={(e) =>
                formik.setFieldValue("Status", parseInt(e.target.value))
              }
              error={formik.touched.Status && formik.errors.Status}
              helperText={formik.touched.Status && formik.errors.Status}
            />

            
          </div>
          <div className=" p-4">
              <AddButton
                text="Submit"
                isLoading={isSubmitting}
                type="Submit"
                disabled={isSubmitting} // ✅ removed !formik.isValid
              />
            </div>
        </form>
      </div>
    </div>
  );
};

export default AddUnion;
